import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import moment from "moment";
import styled from "styled-components";
import { DashboardCharts } from "./DashboardCharts";
import { Filters } from "./Filters";
import DateTimeRangePicker from "@wojtekmaj/react-datetimerange-picker";
import calendarIcon from "../../images/dashboard/Calendar_icon.png";
import "./calendarStyle.css";
import { useTranslation } from "react-i18next";
import filter_Icon from "../../images/dashboard/Filter_Icon.png";
import { MostRecents } from "./MostRecents";
import { PopularGrips } from "./PopularGrips";
import { PopularBalls } from "./PopularBalls";
import theme from "../../theme/feedback";
import { Segmented, SegButton } from "../ui/Segmented";
import {
  setDates,
  getDashboard,
  getNews,
  sortByType,
  sortBySize,
  sortByClients,
  sortByName,
  sortByWeight,
  sortByBalls,
  getVideos,
} from "./dashboard.redux";

const Marginrules = styled.div`
  margin: ${theme.spacing.xl} auto 20vh auto;
  width: 100%;
  max-width: 1024px;
  font-family: ${theme.font.family};

  @media only screen and (max-width: 1163px) and (orientation: landscape) {
    max-width: 84vw;
  }

  @media only screen and (max-width: 991px) {
    max-width: 84vw;
  }
`;

const PageTitle = styled.h1`
  font-family: ${theme.font.family};
  font-size: 28px;
  font-weight: ${theme.font.weightBold};
  color: ${theme.color.text};
  margin: 0;
  padding: 0;
`;

const SectionLabel = styled.h2`
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeXl};
  font-weight: ${theme.font.weightSemibold};
  color: ${theme.color.text};
  margin: 0 0 ${theme.spacing.md} 0;
  padding: 0;
`;

const NewsConntainer = styled.div`
  max-width: 340px;
  border-radius: ${theme.radius.lg};
  overflow: hidden;
  box-shadow: 0px 2px 8px rgba(21, 34, 50, 0.06);
  border: 1px solid ${theme.color.border};
  background: ${theme.color.surface};
  margin: 0 ${theme.spacing.sm};
  flex: 0 0 auto;
  text-align: center;

  @media only screen and (max-width: 991px) {
    max-width: 250px;
  }
`;

const NewsBox = styled.div`
  font-size: ${theme.font.sizeMd};
  display: block;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.5;
  padding: ${theme.spacing.sm};
  max-height: 200px;
  overflow-y: auto;
  text-align: left;
`;

const NewsContainerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: stretch;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.sm};
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing.md};

  @media only screen and (max-width: 991px) {
    width: 100%;
  }
`;

const FilterButton = styled.button`
  cursor: pointer;
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeMd};
  font-weight: ${theme.font.weightSemibold};
  color: ${theme.color.primaryDeep};
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 6px;

  img {
    width: 18px;
    height: 16px;
  }
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.lg};
  align-items: stretch;

  @media only screen and (max-width: 991px) {
    flex-direction: column;
  }
`;

const MainColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

// Positioned slot so the sidebar matches the left column's height without its
// (possibly taller) content pushing the row taller — the card inside is
// absolutely filled and scrolls internally instead of growing past the panels.
const Sidebar = styled.div`
  position: relative;
  width: 326px;
  flex-shrink: 0;
  align-self: stretch;

  @media only screen and (max-width: 991px) {
    position: static;
    width: 84vw;
    align-self: auto;
  }
`;

const PERIODS = ["day", "week", "month", "lifetime", "range"];

const DashboardScreen = ({
  dashboard,
  startingDate,
  endingDate,
  previousStartingDate,
  previousEndingDate,
  loadingDashboard,
  setDates,
  getDashboard,
  userId,
  news,
  videos,
  popularGripsSort,
  popularBallSort,
  getNews,
  sortByType,
  sortBySize,
  sortByClients,
  sortByName,
  sortByWeight,
  sortByBalls,
  getVideos,
}) => {
  const { t } = useTranslation();
  const [paramFilter, setParamFilter] = useState("week");
  const [showFilter, setShowFilter] = useState(true);
  const [markup, setMarkup] = useState([]);
  const [howToVideos, setHowToVideos] = useState([]);

  const onChange = ([sDate, eDate]) => {
    setDates({
      startingDate: moment(sDate).startOf("day").toDate(),
      endingDate: moment(eDate).endOf("day").toDate(),
      previousStartingDate: moment(sDate)
        .subtract(1, "day")
        .startOf("day")
        .toDate(),
      previousEndingDate: moment(sDate)
        .subtract(1, "day")
        .endOf("day")
        .toDate(),
    });
    getDashboard({});
  };

  const handlePeriod = (value) => {
    setParamFilter(value);

    if (value === "day") {
      setDates({
        startingDate: moment().startOf("day").toDate(),
        endingDate: moment().endOf("day").toDate(),
        previousStartingDate: moment()
          .subtract(1, "days")
          .startOf("day")
          .toDate(),
        previousEndingDate: moment().subtract(1, "days").endOf("day").toDate(),
      });
    } else if (value === "week") {
      setDates({
        startingDate: moment().subtract(1, "weeks").startOf("day").toDate(),
        endingDate: moment().endOf("day").toDate(),
        previousStartingDate: moment()
          .subtract(2, "weeks")
          .startOf("day")
          .toDate(),
        previousEndingDate: moment()
          .subtract(1, "weeks")
          .startOf("day")
          .toDate(),
      });
    } else if (value === "month") {
      setDates({
        startingDate: moment().startOf("month").toDate(),
        endingDate: moment().endOf("month").toDate(),
        previousStartingDate: moment()
          .subtract(1, "months")
          .startOf("month")
          .toDate(),
        previousEndingDate: moment()
          .subtract(1, "month")
          .endOf("month")
          .toDate(),
      });
    } else if (value === "lifetime") {
      setDates({
        startingDate: moment().subtract(100, "year").startOf("year").toDate(),
        endingDate: moment().endOf("month").toDate(),
        previousStartingDate: moment()
          .subtract(100, "year")
          .startOf("year")
          .toDate(),
        previousEndingDate: moment().endOf("month").toDate(),
      });
    }

    getDashboard({});
  };

  const handleFilter = () => setShowFilter((prev) => !prev);

  useEffect(() => {
    if (!loadingDashboard) {
      getDashboard({});
    }
  }, [
    loadingDashboard,
    startingDate,
    endingDate,
    previousStartingDate,
    previousEndingDate,
  ]);

  useEffect(() => {
    getNews({});
    getVideos({});
  }, []);

  useEffect(() => {
    if (news) {
      setMarkup(news);
    }
  }, [news]);

  useEffect(() => {
    setHowToVideos(videos?.items);
  }, [videos]);

  return (
    <Marginrules>
      <SectionLabel>{t("general.spectre_news")}</SectionLabel>
      <NewsContainerWrapper>
        {markup.map((markupItem, index) => (
          <NewsConntainer key={index}>
            <a href={markupItem?.link} target="_blank" rel="noreferrer">
              <img
                src={markupItem?._embedded["wp:featuredmedia"][0]?.source_url}
                style={{ width: "340px", height: "auto" }}
                alt=""
              />
            </a>
          </NewsConntainer>
        ))}
      </NewsContainerWrapper>

      <SectionLabel>{t("general.how_to_videos")}</SectionLabel>
      <NewsContainerWrapper>
        {howToVideos &&
          howToVideos?.map((videoItem, index) => (
            <NewsConntainer key={index}>
              <iframe
                width="100%"
                src={`https://www.youtube.com/embed/${videoItem?.snippet?.resourceId?.videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={videoItem?.snippet?.title}
              />
              <div style={{ padding: "0", height: "80px" }}>
                <NewsBox>
                  <strong>{videoItem?.snippet?.title}</strong>
                  <div>
                    <a
                      href={`https://www.youtube.com/watch?v=${videoItem?.snippet?.resourceId?.videoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1a73e8", textDecoration: "none" }}
                    >
                      {t("general.watch_on_youtube")}
                    </a>
                  </div>
                </NewsBox>
              </div>
            </NewsConntainer>
          ))}
      </NewsContainerWrapper>

      <FilterBar>
        <PageTitle>{t("general.dashboard")}</PageTitle>
        <Controls>
          {showFilter && (
            <>
              <Segmented>
                {PERIODS.map((period) => (
                  <SegButton
                    key={period}
                    $active={paramFilter === period}
                    onClick={() => handlePeriod(period)}
                  >
                    {t(`general.${period}`).toUpperCase()}
                  </SegButton>
                ))}
              </Segmented>
              <DateTimeRangePicker
                calendarIcon={<img src={calendarIcon} alt="calendar range" />}
                onChange={onChange}
                value={[new Date(startingDate), new Date(endingDate)]}
                disableClock={true}
                format={"dd MMM yyyy"}
                monthAriaLabel="Month"
              />
            </>
          )}
          <FilterButton onClick={handleFilter}>
            <img src={filter_Icon} alt="" />
            {t("general.filter_by")}
          </FilterButton>
        </Controls>
      </FilterBar>

      <Filters dashboard_data={dashboard} filtered_type={paramFilter} />

      <BottomContainer>
        <MainColumn>
          <PopularGrips
            title={t("polular_grips")}
            dashboard_data={dashboard}
            popularGripsSort={popularGripsSort}
            sortByType={sortByType}
            sortBySize={sortBySize}
            sortByClients={sortByClients}
          />
          <PopularBalls
            title={t("polular_balls")}
            dashboard_data={dashboard}
            popularBallSort={popularBallSort}
            sortByName={sortByName}
            sortByWeight={sortByWeight}
            sortByBalls={sortByBalls}
          />
          <DashboardCharts
            title={t("form.label.pie_charts")}
            dashboard_data={dashboard}
          />
        </MainColumn>
        <Sidebar>
          <MostRecents
            dashboard_data={dashboard}
            customerTitle={t("form.label.most_recent_customers")}
            speecsTitle={t("form.label.most_recent_spec_sheets")}
            customerLabel={t("form.label.customer")}
            userId={userId}
          />
        </Sidebar>
      </BottomContainer>
    </Marginrules>
  );
};

export default connect(
  (state) => ({
    dashboard: state.dashboard.dashboard,
    startingDate: state.dashboard.startingDate,
    endingDate: state.dashboard.endingDate,
    news: state.dashboard.news,
    videos: state.dashboard.videos,
    previousStartingDate: state.dashboard.previousStartingDate,
    previousEndingDate: state.dashboard.previousEndingDate,
    loadingDashboard: state.bowlers.loadingDashboard,
    userId: state.userSession.userData.id,
    popularGripsSort: state.dashboard.popularGripsSort,
    popularBallSort: state.dashboard.popularBallSort,
  }),
  {
    setDates,
    getDashboard,
    sortByType,
    getNews,
    getVideos,
    sortBySize,
    sortByClients,
    sortByBalls,
    sortByName,
    sortByWeight,
  }
)(DashboardScreen);
